// A phone opens on Dashboard every time the app is opened fresh. iOS reopens a closed home-screen app on the LAST page (the address keeps its #/lead-management), so without this the phone "defaults to
// Leads". "Fresh" = the first load in this browser session (sessionStorage is empty); a reload within the session keeps the page, and a link that carries parameters (#/contacts?contactId=...) is
// treated as a real deep link and kept. Returns the page key to start on.
//
// GS-0076 (#2666): the field app must never open in Charlie. A GenServ page in the address (the field.wellrepd.com redirect lands on #genserv-field-wizard) is kept on a fresh phone start, "#/field"
// is the field entry (opens GenServ), and when the last screen the person used was GenServ ('genserv' in LAST_SHELL_KEY) a fresh start or an empty address opens GenServ, not Charlie's Home.
export const PHONE_WIDTH = 768;
export const LAST_SHELL_KEY = 'charlie.lastShell';
export const CHOSE_CHARLIE_KEY = 'charlie.choseCharlie'; // set in sessionStorage by Back to Charlie: the person chose Charlie for this session
export const FIELD_ENTRY_KEY = 'genserv'; // the GenServ page (Today is its first tab)
// PROVISIONAL front-door defaults (WO-2922): host identity wins over last-world memory.
export const FRONT_DOOR_DEFAULTS = Object.freeze({ genservHost: 'genserv.wellrepd.com', charlieHost: 'charlie.wellrepd.com', genservStart: 'genserv-field-wizard', charlieStart: 'dashboard' });
const isGenservKey = (k) => String(k || '').startsWith('genserv');

function readLastShell() {
  try { return (typeof window !== 'undefined' && window.localStorage.getItem(LAST_SHELL_KEY)) || ''; } catch (e) { return ''; }
}
/** Remember which shell the person was last in ('genserv' or 'charlie'). Storage that throws changes nothing. */
export function rememberShell(shell) {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(LAST_SHELL_KEY, shell); } catch (e) { /* private mode */ }
}

export function startKey(hashKey, { width = typeof window !== 'undefined' ? window.innerWidth : 1200, hash = typeof window !== 'undefined' ? window.location.hash : '', hostname = typeof window !== 'undefined' ? window.location.hostname : '', store, lastShell } = {}) {
  const last = lastShell !== undefined ? lastShell : readLastShell();
  const knownHost = hostname === FRONT_DOOR_DEFAULTS.genservHost || hostname === FRONT_DOOR_DEFAULTS.charlieHost;
  if (knownHost) {
    if (hashKey === 'field') return FIELD_ENTRY_KEY;
    if (String(hash || '') === '' || (hashKey === 'dashboard' && !/^#\/?dashboard(?:\?|$)/.test(String(hash || '')))) {
      return hostname === FRONT_DOOR_DEFAULTS.genservHost ? FRONT_DOOR_DEFAULTS.genservStart : FRONT_DOOR_DEFAULTS.charlieStart;
    }
    return hashKey;
  }
  if (hashKey === 'field') return FIELD_ENTRY_KEY;
  const explicitDashboard = /^#\/?dashboard(?:\?|$)/.test(String(hash || ''));
  const open = (key) => (key === 'dashboard' && last === 'genserv' && !explicitDashboard ? FIELD_ENTRY_KEY : key); // an explicit Dashboard link stays in Charlie
  let ss = store;
  try { if (!ss && typeof window !== 'undefined') ss = window.sessionStorage; } catch (e) { ss = null; }
  if (!ss) return open(hashKey);
  let fresh = false;
  try {
    fresh = !ss.getItem('charlie.booted');
    ss.setItem('charlie.booted', '1');
  } catch (e) { return open(hashKey); }
  if (fresh && width < PHONE_WIDTH && !String(hash).includes('?') && !isGenservKey(hashKey)) return open('dashboard');
  return open(hashKey);
}

/** A person with GenServ access, on a phone, with only the default Dashboard asked for and no Charlie choice this session, starts in GenServ. */
export const phoneStartsInGenserv = ({ access, key, width, choseCharlie, explicitDashboard = false, hostname = '' }) => access === true && key === 'dashboard' && width < PHONE_WIDTH && !choseCharlie && !explicitDashboard && hostname !== FRONT_DOOR_DEFAULTS.charlieHost && hostname !== FRONT_DOOR_DEFAULTS.genservHost;

/** The saved partner was put back after GenServ was entered and the person did not pick it (their pick bumps the signal): GenServ must be entered again. */
export const genservOverridden = ({ entered, genservActive, signal, entrySignal }) => !!entered && !genservActive && signal === entrySignal;

/**
 * GS-0076: one failed access check must not decide where the phone lands. Runs the check (a function returning { data, error }), tries again after a short wait when it fails
 * (a thrown error counts as a failure), and returns { data, error } of the last try. The caller treats a final error as "not known yet" (null), never as "no access".
 */
export async function withRetry(run, { attempts = 3, delayMs = 1500, sleep = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
  let last = { data: null, error: new Error('not run') };
  for (let i = 0; i < attempts; i += 1) {
    try { last = await run(); } catch (e) { last = { data: null, error: e || new Error('failed') }; }
    if (!last || !last.error) return last || { data: null, error: null };
    if (i < attempts - 1) await sleep(delayMs * (i + 1));
  }
  return last;
}

/** The access answer for routing: true / false from a good answer, null (unknown) when the check failed. */
export const accessFrom = ({ data, error }) => (error ? null : !!(data && data.length));
